import { useEffect, useState } from "react";
import { Button, Checkbox, Image, Input, Modal } from "antd";
import { BlobDto } from "../../Repository/eventbookyapi";
import { DocumentService } from "../../Services/ImageService";
import { TextService } from "../../Services/TextService";
import { EmailTemplateType } from "../../Types/EmailTemplate";
import ColorPicker from "../Color/ColorPicker";
import EmailDesignTemplateCardSelect from "../Select/EmailDesignTemplateCardSelect";
import ImageSelect from "../Select/ImageSelect";

const DEFAULT_ACCENT_COLOR = "#4a6cf7";
const DEFAULT_TEXT_COLOR = "#ffffff";

/** The design template an invite or a reminder for an event is sent in. */
export type eventMailDesign = {
    emailDesignTemplate: EmailTemplateType,
    header?: string,
    imageUrl?: string,
    accentColor?: string,
    textColor?: string
}

/** The text id of what is missing in the design, or undefined when it can be used. */
export const getEventMailDesignError = (design?: eventMailDesign): string | undefined => {
    if (design?.emailDesignTemplate === EmailTemplateType.Classic && !design.imageUrl) {
        return "CreateTemplate_LogoRequired";
    }

    if (design?.emailDesignTemplate === EmailTemplateType.Header && !design.header?.trim()) {
        return "EventMail_HeaderRequired";
    }

    return undefined;
};

/**
 * Lets the admin choose a design template for an invite or a reminder, the same templates as for
 * other mails to members. Reports the chosen design, or undefined for the plain mail.
 */
export const EventMailDesign = (props: { onChange: (design?: eventMailDesign) => void }) => {
    const GetText = TextService.GetText;
    const [enabled, setEnabled] = useState(false);
    const [templateType, setTemplateType] = useState(EmailTemplateType.Classic as EmailTemplateType);
    const [header, setHeader] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
    const [images, setImages] = useState([] as BlobDto[]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    const isClassic = templateType === EmailTemplateType.Classic;
    const isHeader = templateType === EmailTemplateType.Header;

    useEffect(() => {
        // Only the fields of the chosen template are sent, like in the form for other mails.
        props.onChange(enabled ? {
            emailDesignTemplate: templateType,
            header: isHeader ? header : undefined,
            imageUrl: isClassic ? imageUrl : undefined,
            accentColor: isHeader ? accentColor : undefined,
            textColor: isHeader ? textColor : undefined,
        } : undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, templateType, header, imageUrl, accentColor, textColor]);

    const loadImages = async () => {
        setImages(await DocumentService.GetPublicImages());
    };

    const openImagePicker = async () => {
        await loadImages();
        setIsImageModalOpen(true);
    };

    const labelStyle = { marginTop: "12px", marginBottom: "4px" };

    return <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)}>{GetText("CreateTemplate_AddDesign")}</Checkbox>

        {enabled ? <>
            <div style={labelStyle}>{GetText("CreateTemplate_DesignTemplate")}</div>
            <EmailDesignTemplateCardSelect value={templateType} onChange={setTemplateType} />

            {isClassic ? <>
                <div style={labelStyle}><span style={{ color: "red" }}>*</span> {GetText("CreateTemplate_Logo")}</div>
                <Button onClick={openImagePicker}>{GetText("CreateTemplate_SelectLogo")}</Button>
                {imageUrl ? <div style={{ marginTop: "10px" }}><Image width={220} height={140} preview={true} src={imageUrl}></Image></div> : ""}
            </> : ""}

            {isHeader ? <>
                <div style={labelStyle}><span style={{ color: "red" }}>*</span> {GetText("CreateTemplate_Header")}</div>
                <Input value={header} onChange={(e) => setHeader(e.target.value)} style={{ width: "100%", maxWidth: "400px" }} />

                <div style={labelStyle}>{GetText("CreateTemplate_AccentColor")}</div>
                <ColorPicker color={accentColor} onChangeComplete={setAccentColor} />

                <div style={labelStyle}>{GetText("CreateTemplate_TextColor")}</div>
                <ColorPicker color={textColor} onChangeComplete={setTextColor} />
            </> : ""}
        </> : ""}

        <Modal
            title={GetText("MyEventDetails_SelectImage")}
            width={850}
            open={isImageModalOpen}
            onOk={() => { setIsImageModalOpen(false) }}
            onCancel={() => { setIsImageModalOpen(false) }}
        >
            <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={setImageUrl} selectedImage={imageUrl} />
        </Modal>
    </div>;
};
